import { ElectronApi } from '@/services/electron.api';
import { Shape } from '@/services/shape';
import { ResourceKey } from '@/services/shape/attributes';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { getImageSize, isImageFile } from '@/utils/fs/image.utils';
import { sortFilenames } from '@/utils/fs/name.utils';
import { pickDir } from '@/utils/fs/pick-file.utils';
import { Modal } from 'ant-design-vue';
import { useWatchfaceImageValid } from './use-watchface-image-valid';

export const useShapeSprite = () => {
  const editorStore = useEditorStore();
  const watchStore = useWatchfaceStore();
  const { isSameSize } = useWatchfaceImageValid();

  const uploadImages = async (data: {
    shape: Shape;
    imageFilenames: string[];
    dirname: string;
    resourceName: ResourceKey;
    colorId: string;
    clear?: boolean;
  }) => {
    const {
      shape,
      imageFilenames,
      dirname,
      clear = false,
      resourceName,
      colorId
    } = data;

    // 先删除旧数据
    if (clear) {
      shape.deleteResource({
        resourceName,
        colorGroup: colorId
      });
    }

    await shape?.uploadResources({
      resourceName,
      colorGroup: colorId,
      realpaths: imageFilenames,
      dirname,
      resize: resourceName == 'images' ? true : false
    });
  };

  const _valid = async (images: string[]) => {
    const baseMaxCount = ElectronApi.isProfessional() ? 200 : 60;
    const { count: maxCount = baseMaxCount, size } =
      watchStore.watchfaceDevice?.spriteRules ?? {};
    const maxWidth = Math.min(
      watchStore.size.width,
      size?.w ?? Number.MAX_VALUE
    );
    const maxHeight = Math.min(
      watchStore.size.height,
      size?.h ?? Number.MAX_VALUE
    );

    if (images.length < 1) {
      Modal.error({
        title: '上传失败',
        content: '该目录下不存在序列帧图片（图片仅支持png格式）'
      });
      return false;
    } else if (images.length > maxCount) {
      Modal.error({
        title: '上传失败',
        content: '序列帧数量不可超过' + maxCount + '张'
      });
      return false;
    }
    // 验证图片大小是否一致
    if (!(await isSameSize(images))) {
      Modal.error({
        title: '上传失败',
        content: '序列帧图片大小不一，请保存一致'
      });
      return false;
    }
    // 验证图片大小
    const { height = 0, width = 0 } = (await getImageSize(images[0])) ?? {};
    if (width > maxWidth || height > maxHeight) {
      Modal.error({
        title: '上传失败',
        content: `图片大小不可超过${maxWidth}*${maxHeight}`
      });
      return false;
    }

    return true;
  };

  const pickThenUploadSpriteImages = async (
    shape: Shape,
    options?: {
      clear?: boolean;
      resourceName?: ResourceKey;
    }
  ) => {
    const path = ElectronApi.path;
    if (!path) return;

    const { clear = false, resourceName = 'images' } = options ?? {};

    // 选择序列帧文件夹
    const dir = (await pickDir()) as string | undefined;
    // 未选择时，不可添加
    if (!dir) return;
    // 选择该目录下所有图片
    const files = await ElectronApi.fs?.promises.readdir(dir);
    if (!files) return;
    // 排序
    sortFilenames(files);

    const images: string[] = [];
    files
      .filter((file) => isImageFile(file))
      .forEach((file) => {
        images.push(ElectronApi.path?.join(dir, file) ?? '');
      });

    // 序列帧校验
    const valid = await _valid(images);
    if (!valid) return;

    // 上传
    const colorId = editorStore.themeColor?.id ?? '';
    const dirname = path.basename(dir);
    await uploadImages({
      shape,
      imageFilenames: images,
      dirname,
      clear,
      resourceName: resourceName,
      colorId
    });

    // 如果不是images，则不更新图片大小
    if (resourceName != 'images') {
      // 更新图片大小
      const { width: w = 0, height: h = 0 } =
        shape.getImageSize(resourceName, colorId) ?? {};
      shape.setAttrs({
        spriteExtra:
          shape.attrs.spriteExtra?.map((item) => {
            if (item.spriteImagesName == resourceName) {
              return {
                ...item,
                w,
                h
              };
            }
            return item;
          }) ?? []
      });
    }

    return true;
  };

  return {
    pickThenUploadSpriteImages
  };
};
